import React from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, TrendingUp } from "lucide-react";
import { getSupabaseProductImageUrl } from "../../../../utils/imageUtils";

interface ProductCardProps {
  product: any;
  adding?: boolean;
  onAddToCart?: () => void;
}

export const ProductListCard: React.FC<ProductCardProps> = ({
  product,
  adding = false,
  onAddToCart,
}) => {
  const navigate = useNavigate();

  // Handle new string[] format or old {path: string}[] format
  const images = product.images || [];
  const lastImageEntry = images[images.length - 1];
  const lastImage =
    typeof lastImageEntry === "string"
      ? lastImageEntry
      : (lastImageEntry as any)?.path;
  const imageUrl = getSupabaseProductImageUrl(lastImage);

  // Use the pre-calculated prices from the hook transformations
  // If on sale show regular price
  const displayPrice = product.price ?? 0;
  const originalPrice = product.original_price ?? product.price;
  const saleRate = product.sale_rate ?? product.discount ?? 0;
  const isOnSale = product.on_sale;

  return (
    <div
      className="group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer h-auto sm:h-48"
      onClick={() => navigate(`/product/${product.id}`)}
    >
      <div className="flex flex-col sm:flex-row h-full">
        {/* Image Section */}
        <div className="relative w-full h-48 sm:w-56 sm:h-full flex-shrink-0 bg-gray-50 overflow-hidden">
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
            {/* Feature Badges - Shown first */}
            {product.sortType === "featured" && (
              <span className="bg-yellow-500 text-white text-xs font-semibold px-2.5 py-1 rounded shadow-md">
                Featured
              </span>
            )}
            {product.is_new && (
              <span className="bg-emerald-500 text-white text-xs font-semibold px-2.5 py-1 rounded shadow-md">
                NEW
              </span>
            )}
            {product.trending && (
              <span className="bg-purple-500 text-white text-xs font-semibold px-2.5 py-1 rounded shadow-md flex items-center gap-1">
                <TrendingUp size={12} />
                HOT
              </span>
            )}
            {/* Sale Rate Badge - Always shown if product is on sale */}
            {isOnSale && saleRate > 0 && (
              <span className="bg-red-500 text-white text-xs font-semibold px-2.5 py-1 rounded shadow-md">
                -{saleRate}%
              </span>
            )}
          </div>

          {/* Product Image */}
          <img
            src={imageUrl || product.image || "/assets/images/no-image.png"}
            alt={product.name || "Product"}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              if (e.currentTarget.src !== "/assets/images/no-image.png") {
                e.currentTarget.src = "/assets/images/no-image.png";
              }
            }}
          />
        </div>

        {/* Content Section */}
        <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-w-0">
          <div className="flex-1">
            {/* Brand */}
            {product.brand_name && (
              <div className="text-xs text-gray-500 mb-1.5 uppercase tracking-wide font-medium">
                {product.brand_name}
              </div>
            )}

            {/* Product Name */}
            <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 text-base sm:text-lg group-hover:text-primary-600 transition-colors">
              {product.name}
            </h3>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-3.5 h-3.5 text-yellow-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.966a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.921-.755 1.688-1.54 1.118l-3.38-2.455a1 1 0 00-1.175 0l-3.38 2.455c-.784.57-1.838-.197-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.174 9.393c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.966z" />
                  </svg>
                ))}
              </div>
              <span className="text-xs text-gray-600">(5.0)</span>
            </div>

            {/* Description */}
            {product.description && (
              <p className="text-sm text-gray-600 mb-3 line-clamp-1 leading-relaxed">
                {product.description}
              </p>
            )}
          </div>

          {/* Price and Action */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-1 border-t border-gray-100">
            {/* Price */}
            <div className="flex items-baseline gap-2">
              <span className="text-xl sm:text-2xl font-bold text-gray-900">
                ${displayPrice.toFixed(2)}
              </span>
              {isOnSale && originalPrice && originalPrice > displayPrice && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400 line-through">
                    ${originalPrice.toFixed(2)}
                  </span>
                  <span className="text-xs text-green-600 font-semibold">
                    Save ${(originalPrice - displayPrice).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Add to Cart Button */}
            <button
              className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-lg flex items-center justify-center gap-2 font-semibold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-md hover:shadow-lg text-sm"
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart?.();
              }}
              disabled={adding || product.quantity <= 0}
            >
              <ShoppingCart
                className={`w-4 h-4 ${adding ? "animate-pulse" : ""}`}
              />
              <span>
                {adding
                  ? "Adding..."
                  : product.quantity <= 0
                    ? "Out of Stock"
                    : "Add To Cart"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
